import type { UIMessage } from "ai";

const HIDDEN_TOOLS = new Set(["search_tools", "provide_suggestions"]);

const TERMINAL_TOOL_STATES = new Set([
  "output-available",
  "output-error",
  "output-denied",
]);

const TOOL_LABELS: Record<string, string> = {
  // Intelligence — company
  intelligence_get_company_profile: "Hämtar företagsprofil",

  // Intelligence — teams & members
  intelligence_list_teams: "Söker säljteam",
  intelligence_list_members: "Söker säljare",
  intelligence_get_member: "Hämtar säljare",

  // Intelligence — assessments
  intelligence_list_assessments: "Hämtar tester",
  intelligence_get_assessment: "Hämtar testresultat",

  // Intelligence — vault
  intelligence_search_documents: "Söker i dokumentvalvet",
  intelligence_read_document: "Läser dokument",

  // Intelligence — actions
  intelligence_list_actions: "Hämtar åtgärder",
  intelligence_accept_action: "Startar åtgärd",
  intelligence_complete_action: "Slutför åtgärd",

  // Intelligence — action plan
  intelligence_generate_action_plan_pdf: "Skapar handlingsplan",

  // Intelligence — pulse
  intelligence_get_pulse_history: "Hämtar Pulse-historik",
  intelligence_get_team_pulse: "Hämtar team-Pulse",
  intelligence_upsert_pulse_score: "Sparar Pulse-betyg",

  // Intelligence — memory
  intelligence_save_to_memory: "Sparar till minnet",

  // Intelligence — KPIs
  intelligence_get_kpis: "Hämtar nyckeltal",
  intelligence_get_kpi_history: "Hämtar nyckeltalshistorik",
  intelligence_get_store_measures: "Hämtar butikens siffror",

  // CRM — generic scoped reads (HubSpot, Garba)
  crm_list_linked_sellers: "Kontrollerar kopplade säljare",
  crm_count_calls: "Räknar samtal",
  crm_list_calls: "Hämtar samtal",
  crm_get_call_transcript: "Läser samtalstranskript",
  crm_list_meetings: "Hämtar möten",
  crm_list_tasks: "Hämtar uppgifter",
  crm_list_deals: "Hämtar affärer",
  crm_pipeline_summary: "Summerar pipeline",
  crm_list_stages: "Hämtar faser",
  crm_list_stage_entries: "Hämtar fasändringar",

  // CRM — Upsales' own tools
  crm_upsales_list_stages: "Hämtar faser från Upsales",
  crm_upsales_list_opportunities: "Hämtar affärer från Upsales",
  crm_upsales_get_pipeline_summary: "Summerar pipeline i Upsales",
  crm_upsales_list_appointments: "Hämtar möten från Upsales",
  crm_upsales_count_appointments: "Räknar möten i Upsales",
  crm_upsales_list_activities: "Hämtar aktiviteter från Upsales",
  crm_upsales_list_phone_calls: "Hämtar samtal från Upsales",
  crm_upsales_get_call_summary: "Hämtar samtalssammanfattning från Upsales",

  // Recruit
  recruit_list_job_ads: "Hämtar jobbannonser",
  recruit_get_job_ad: "Hämtar jobbannons",
  recruit_list_matches: "Hämtar kandidatmatchningar",

  // Leads
  leads_list_lead_ads: "Hämtar leadsannonser",
  leads_get_lead_ad: "Hämtar leadsannons",
  leads_get_lead_stats: "Hämtar leadsstatistik",

  // Web search
  web_search: "Söker på webben",

  // Connected apps (Composio meta-tools)
  COMPOSIO_SEARCH_TOOLS: "Söker bland anslutna appar",
  COMPOSIO_GET_TOOL_SCHEMAS: "Förbereder åtgärd",
  COMPOSIO_MANAGE_CONNECTIONS: "Kontrollerar appanslutning",
  COMPOSIO_MULTI_EXECUTE_TOOL: "Utför åtgärd",
  COMPOSIO_REMOTE_WORKBENCH: "Bakgrundsagent arbetar",
  COMPOSIO_REMOTE_BASH_TOOL: "Bakgrundsagent arbetar",
};

const GERUNDS: Record<string, string> = {
  list: "Söker",
  get: "Hämtar",
  fetch: "Hämtar",
  create: "Skapar",
  update: "Uppdaterar",
  delete: "Tar bort",
  search: "Söker",
  export: "Exporterar",
  send: "Skickar",
  start: "Startar",
  stop: "Stoppar",
  match: "Matchar",
  sync: "Synkar",
  pause: "Pausar",
  resume: "Återupptar",
  cancel: "Avbryter",
  duplicate: "Duplicerar",
};

type DynamicToolPart = {
  type: "dynamic-tool";
  toolName?: string;
  state?: string;
  toolCallId?: string;
  output?: unknown;
};

type StaticToolPart = {
  type: string;
  state?: string;
  toolCallId?: string;
  output?: unknown;
};

type LegacyToolInvocationPart = {
  type: "tool-invocation";
  toolName?: string;
  toolInvocationId?: string;
  state?: string;
  result?: unknown;
};

export type ToolPartLike = DynamicToolPart | StaticToolPart | LegacyToolInvocationPart;

export type NormalizedToolPart = {
  toolName: string;
  state: string;
  toolCallId: string;
  output?: unknown;
};

/** A `data-<name>` part as it sits in `message.parts`. */
export type DataPartLike = { type: string; id?: string; data?: unknown; transient?: boolean };

export type ParsedAssistantParts = {
  toolParts: NormalizedToolPart[];
  visibleTools: NormalizedToolPart[];
  /** Typed UI parts to render under the text, last part per id wins. */
  dataParts: DataPartLike[];
  textContent: string;
  showThinking: boolean;
  hasContent: boolean;
  /** True when the model ran tools but all were hidden (e.g. only provide_suggestions).
   *  The message has no visible content but is not a failure — suggestions appear elsewhere. */
  hasOnlyHiddenTools: boolean;
};

export function isToolPart(part: { type?: string }): part is ToolPartLike {
  if (!part.type) return false;
  return (
    part.type === "dynamic-tool" ||
    part.type.startsWith("tool-") ||
    part.type === "tool-invocation"
  );
}

export function normalizeToolPart(part: ToolPartLike): NormalizedToolPart {
  if (part.type === "dynamic-tool") {
    const dynamicPart = part as DynamicToolPart;
    return {
      toolName: dynamicPart.toolName ?? "tool",
      state: dynamicPart.state ?? "call-started",
      toolCallId:
        dynamicPart.toolCallId ?? `dynamic-${dynamicPart.toolName ?? "tool"}`,
      output: dynamicPart.output,
    };
  }

  if (part.type === "tool-invocation") {
    const legacyPart = part as LegacyToolInvocationPart;
    return {
      toolName: legacyPart.toolName ?? "tool",
      state: legacyPart.state ?? "call",
      toolCallId:
        legacyPart.toolInvocationId ?? `invocation-${legacyPart.toolName ?? "tool"}`,
      output: legacyPart.result,
    };
  }

  const staticPart = part as StaticToolPart;
  return {
    toolName: staticPart.type.replace(/^tool-/, "") || "tool",
    state: staticPart.state ?? "call-started",
    toolCallId: staticPart.toolCallId ?? `tool-${staticPart.type}`,
    output: staticPart.output,
  };
}

export function isToolDone(state: string): boolean {
  return TERMINAL_TOOL_STATES.has(state) || state === "result";
}

/**
 * Which system a tool reads, for the icon next to its label. The model's tool
 * names are internal; the user knows the integration. CRM tools carry the
 * CRM's display name in their output (`crm: "Upsales"`), which decides the
 * icon for the generic `crm_*` reads that serve several CRMs.
 */
export type ToolIntegration =
  | { kind: "graftus" }
  | { kind: "app"; name: string; domain: string }
  | { kind: "crm"; name: string | null }
  | { kind: "web" }
  | { kind: "generic" };

const CRM_DOMAINS: Record<string, { name: string; domain: string }> = {
  upsales: { name: "Upsales", domain: "upsales.com" },
  hubspot: { name: "HubSpot", domain: "hubspot.com" },
};

const COMPOSIO_APP_DOMAINS: Record<string, { name: string; domain: string }> = {
  slack: { name: "Slack", domain: "slack.com" },
  gmail: { name: "Gmail", domain: "gmail.com" },
  googlecalendar: { name: "Google Calendar", domain: "calendar.google.com" },
  hubspot: { name: "HubSpot", domain: "hubspot.com" },
  salesforce: { name: "Salesforce", domain: "salesforce.com" },
  pipedrive: { name: "Pipedrive", domain: "pipedrive.com" },
  outlook: { name: "Outlook", domain: "outlook.com" },
};

export function getToolIntegration(toolName: string, output?: unknown): ToolIntegration {
  const lower = toolName.toLowerCase();
  if (lower.startsWith("crm_")) {
    const own = lower.match(/^crm_([a-z0-9]+)_/)?.[1];
    const fromName = own ? CRM_DOMAINS[own] : undefined;
    if (fromName) return { kind: "app", ...fromName };
    const crm =
      output && typeof output === "object" && typeof (output as { crm?: unknown }).crm === "string"
        ? ((output as { crm: string }).crm as string)
        : null;
    const fromOutput = crm ? CRM_DOMAINS[crm.toLowerCase()] : undefined;
    if (fromOutput) return { kind: "app", ...fromOutput };
    return { kind: "crm", name: crm };
  }
  if (
    lower.startsWith("intelligence_") ||
    lower.startsWith("recruit_") ||
    lower.startsWith("leads_")
  ) {
    return { kind: "graftus" };
  }
  if (lower.startsWith("web_search")) return { kind: "web" };
  for (const [prefix, app] of Object.entries(COMPOSIO_APP_DOMAINS)) {
    if (lower.startsWith(prefix + "_")) return { kind: "app", ...app };
  }
  return { kind: "generic" };
}

/**
 * Labels are written for the running state ("Räknar möten i Upsales"). Once
 * the tool is done the same label in past tense ("Räknade möten i Upsales")
 * says so without a second table; a verb we do not know keeps the label.
 */
const PAST_TENSE: Record<string, Record<string, string>> = {
  sv: {
    Hämtar: "Hämtade",
    Räknar: "Räknade",
    Söker: "Sökte",
    Läser: "Läste",
    Kontrollerar: "Kontrollerade",
    Summerar: "Summerade",
    Skapar: "Skapade",
    Sparar: "Sparade",
    Startar: "Startade",
    Slutför: "Slutförde",
    Utför: "Utförde",
    Förbereder: "Förberedde",
    Uppdaterar: "Uppdaterade",
    Matchar: "Matchade",
    Synkar: "Synkade",
    Skickar: "Skickade",
    Exporterar: "Exporterade",
  },
  en: {
    Fetching: "Fetched",
    Counting: "Counted",
    Searching: "Searched",
    Reading: "Read",
    Checking: "Checked",
    Summarising: "Summarised",
    Creating: "Created",
    Saving: "Saved",
    Starting: "Started",
    Completing: "Completed",
    Running: "Ran",
    Preparing: "Prepared",
    Updating: "Updated",
    Matching: "Matched",
    Syncing: "Synced",
    Sending: "Sent",
    Exporting: "Exported",
  },
};

export function toDoneLabel(label: string, language: string): string {
  const verbs = PAST_TENSE[language.toLowerCase().slice(0, 2)];
  if (!verbs) return label;
  const space = label.indexOf(" ");
  const verb = space === -1 ? label : label.slice(0, space);
  const past = verbs[verb];
  return past ? past + label.slice(verb.length) : label;
}

export function formatToolName(name: string): string {
  if (TOOL_LABELS[name]) return TOOL_LABELS[name];

  const parts = name.toLowerCase().split("_").filter(Boolean);
  if (parts.length < 2) return name.replace(/_/g, " ");

  const action = parts[parts.length - 1]!;
  const noun = parts.slice(0, -1).join(" ");
  const verb = GERUNDS[action];
  if (verb) return `${verb} ${noun}`;

  return name.replace(/_/g, " ");
}

/**
 * Joins text parts, dropping a block that repeats the previous one. The model
 * occasionally writes its whole answer again after a late tool call (seen
 * after provide_suggestions, GRA-108); the server now stops the turn there,
 * and this keeps an older message or a different agent from showing it twice.
 */
export function joinTextParts(texts: readonly string[]): string {
  const kept: string[] = [];
  for (const text of texts) {
    const trimmed = text.trim();
    const previous = kept.length > 0 ? kept[kept.length - 1]!.trim() : null;
    if (trimmed.length > 0 && previous !== null && trimmed === previous) continue;
    kept.push(text);
  }
  return kept.join("");
}

export function parseAssistantParts(
  message: UIMessage,
  opts: { isStreaming: boolean },
): ParsedAssistantParts {
  const toolParts: NormalizedToolPart[] = message.parts
    .filter((part) => isToolPart(part as { type?: string }))
    .map((part) => normalizeToolPart(part as ToolPartLike));

  // Only count visible (non-hidden) tools when determining where text may appear.
  // Hidden tools like `provide_suggestions` run after the text response, so they
  // must not push the text before lastToolIndex and cause it to be filtered out.
  const lastVisibleToolIndex = message.parts.reduce(
    (max, part, index) => {
      if (!isToolPart(part as { type?: string })) return max;
      const normalized = normalizeToolPart(part as ToolPartLike);
      if (HIDDEN_TOOLS.has(normalized.toolName)) return max;
      return Math.max(max, index);
    },
    -1,
  );
  const hasVisibleTools = lastVisibleToolIndex !== -1;

  const toolsInProgress = toolParts.length > 0 && toolParts.some((part) => !isToolDone(part.state));

  const rawText = (() => {
    if (opts.isStreaming && toolsInProgress) return "";
    // Prefer text that appears after the last visible tool (the final response).
    const afterTools = message.parts
      .filter(
        (part, index): part is { type: "text"; text: string } =>
          part.type === "text" && (!hasVisibleTools || index > lastVisibleToolIndex),
      )
      .map((part) => part.text);
    const afterToolsText = joinTextParts(afterTools);
    if (afterToolsText.trim()) return afterToolsText;
    // Fallback: some agent runs emit a visible tool AFTER the text (e.g. a
    // Composio follow-up step). In that case `afterTools` is empty even though
    // the text exists — return all text so the response is never lost.
    return joinTextParts(
      message.parts
        .filter((part): part is { type: "text"; text: string } => part.type === "text")
        .map((part) => part.text),
    );
  })();

  const textContent = rawText.trim() ? rawText : "";
  const visibleTools = toolParts.filter((part) => !HIDDEN_TOOLS.has(part.toolName));
  // Data parts: anything `data-*` that is not transient; a later part with the
  // same id replaces the earlier one (a table filling in).
  const byId = new Map<string, DataPartLike>();
  for (const part of message.parts as DataPartLike[]) {
    if (typeof part.type !== "string" || !part.type.startsWith("data-")) continue;
    if (part.transient) continue;
    byId.set(part.id ?? `${part.type}#${byId.size}`, part);
  }
  const dataParts = [...byId.values()];
  // Show the thinking shimmer from the first byte until text arrives, also
  // while a visible tool runs: it sits where the text will appear, and hiding
  // it during the tool and bringing it back for the gap before the text made
  // it flicker. The tool pill carries its own running state.
  const showThinking = opts.isStreaming && !textContent;
  const hasContent = Boolean(textContent) || visibleTools.length > 0 || dataParts.length > 0;
  const hasOnlyHiddenTools = toolParts.length > 0 && visibleTools.length === 0;

  return {
    toolParts,
    visibleTools,
    dataParts,
    textContent,
    showThinking,
    hasContent,
    hasOnlyHiddenTools,
  };
}

export function extractAiSuggestions(message: UIMessage): string[] {
  for (const part of message.parts) {
    if (!isToolPart(part as { type?: string })) continue;
    const normalized = normalizeToolPart(part as ToolPartLike);
    if (normalized.toolName !== "provide_suggestions") continue;
    if (!isToolDone(normalized.state)) continue;
    const out = normalized.output as { suggestions?: unknown } | undefined;
    if (!Array.isArray(out?.suggestions)) continue;
    return out.suggestions.filter((s): s is string => typeof s === "string");
  }
  return [];
}
